import { Trans, t } from "@lingui/macro";
import { useLingui } from "@lingui/react";
import cx from "classnames";
import { ethers } from "ethers";
import { useEffect, useMemo, useRef, useState } from "react";
import useSWR from "swr";
import type { Hash } from "viem";

import CheckIcon from "assets/icons/CheckIcon";
import CopyIcon from "assets/icons/CopyIcon";
import { REFERRAL_CODE_KEY } from "config/localStorage";
import { useAuth } from "context/AuthContext/AuthProvider";
import { useGlobalContext } from "context/GlobalContext/GlobalContextProvider";
import { usePendingTxns } from "context/PendingTxnsContext/PendingTxnsContext";
import {
  getReferralCodeOwner,
  setTraderReferralCodeByUser,
  useAffiliateTier,
  useReferrerDiscountShare,
  useTiers,
  validateReferralCodeExists,
} from "domain/referrals/hooks";
import { getReferralCodeLookup, upsertTrader } from "lib/apis/referral";
import { helperToast } from "lib/helperToast";
import { isAddressZero, MAX_REFERRAL_CODE_LENGTH, shortenAddress } from "lib/legacy";
import { useDebounce } from "lib/useDebounce";
import useWallet from "lib/wallets/useWallet";
import { encodeReferralCode } from "sdk/utils/referrals";
import { decodeReferralCode } from "sdk/utils/referrals";

import Button from "components/Button/Button";
import TooltipWithPortal from "components/Tooltip/TooltipWithPortal";

import { getReferralCodeTradeUrl, REFERRAL_CODE_REGEX } from "./referralsHelper";

function JoinReferralCode({ active }: { active: boolean }) {
  const { setConnectWalletModalOpen } = useGlobalContext();
  return (
    <div className="referral-card section-center mt-medium">
      <h2 className="title text-h2">
        <Trans>Enter Referral Code</Trans>
      </h2>
      <p className="sub-title">
        <Trans>Please input a referral code to benefit from fee discounts.</Trans>
      </p>
      <div className="card-action">
        {active ? (
          <ReferralCodeForm />
        ) : (
          <Button
            variant="primary"
            className="w-full !rounded-4 !py-8"
            type="submit"
            onClick={() => setConnectWalletModalOpen(true)}
          >
            <Trans>Connect Wallet</Trans>
          </Button>
        )}
      </div>
    </div>
  );
}

export function ReferralCodeForm({
  callAfterSuccess = undefined,
  userReferralCodeString = "",
  type = "join",
}: {
  callAfterSuccess?: () => void;
  userReferralCodeString?: string;
  type?: string;
}) {
  useLingui();
  const { account, signer, chainId } = useWallet();
  const { isAuthenticated, signIn } = useAuth();
  const currentChainId = chainId ?? 0;
  const [referralCode, setReferralCode] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);
  const [isValidating, setIsValidating] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [referralCodeExists, setReferralCodeExists] = useState(true);
  const { pendingTxns, setPendingTxns } = usePendingTxns();
  const debouncedReferralCode = useDebounce(referralCode, 300);
  const storedReferralCode = useMemo(() => {
    const encodedCode = window.localStorage.getItem(REFERRAL_CODE_KEY);
    if (!encodedCode || !encodedCode.startsWith("0x")) return undefined;
    try {
      const decodedCode = decodeReferralCode(encodedCode as Hash);
      if (decodedCode && decodedCode.length <= MAX_REFERRAL_CODE_LENGTH) {
        return decodedCode;
      }
    } catch (e) {
      return undefined;
    }
    return undefined;
  }, []);

  const shouldLookupReferralCode =
    currentChainId > 0 && debouncedReferralCode !== "" && REFERRAL_CODE_REGEX.test(debouncedReferralCode);

  const { data: referralLookup } = useSWR(
    shouldLookupReferralCode ? ["referrals:codeLookup", currentChainId, debouncedReferralCode.toLowerCase()] : null,
    () => getReferralCodeLookup(debouncedReferralCode, { chainId: currentChainId }),
    {
      dedupingInterval: 30_000,
      revalidateOnFocus: false,
    }
  );

  const lookupReferrer = useMemo(() => {
    const candidate = referralLookup?.referrer || referralLookup?.owner;
    if (!candidate || !ethers.isAddress(candidate)) {
      return undefined;
    }
    return ethers.getAddress(candidate);
  }, [referralLookup?.owner, referralLookup?.referrer]);

  const isSelfReferral = useMemo(() => {
    if (!lookupReferrer || !account || !ethers.isAddress(account)) {
      return false;
    }

    return ethers.getAddress(lookupReferrer) === ethers.getAddress(account);
  }, [account, lookupReferrer]);

  const { affiliateTier: inviterTier } = useAffiliateTier(signer, currentChainId, lookupReferrer);
  const { totalRebate, discountShare: defaultDiscountShare } = useTiers(signer, currentChainId, inviterTier);
  const { discountShare: customDiscountShare } = useReferrerDiscountShare(signer, currentChainId, lookupReferrer);
  const effectiveDiscountShare = useMemo(
    () => ((customDiscountShare ?? 0n) > 0n ? customDiscountShare : defaultDiscountShare),
    [customDiscountShare, defaultDiscountShare]
  );
  const discountPercent = useMemo(
    () => (effectiveDiscountShare !== undefined ? Number(effectiveDiscountShare) / 100 : undefined),
    [effectiveDiscountShare]
  );

  function getPrimaryText() {
    if (isSubmitting) {
      return t`Adding...`;
    }
    if (debouncedReferralCode === "") {
      return t`Done`;
    }
    if (isValidating) {
      return t`Checking...`;
    }

    return t`Done`;
  }
  function isPrimaryEnabled() {
    if (
      debouncedReferralCode === "" ||
      isSubmitting ||
      isValidating ||
      !referralCodeExists ||
      debouncedReferralCode === userReferralCodeString ||
      isSelfReferral
    ) {
      return false;
    }
    return true;
  }

  async function handleSubmit(event) {
    const isEdit = type === "edit";
    event.preventDefault();

    if (isSelfReferral) {
      helperToast.error(t`You can't use your own referral code.`);
      return;
    }

    let resolvedReferralOwnerAddress = lookupReferrer;
    if (!resolvedReferralOwnerAddress) {
      try {
        const onChainOwner = await getReferralCodeOwner(currentChainId, encodeReferralCode(referralCode));
        if (onChainOwner && !isAddressZero(onChainOwner)) {
          resolvedReferralOwnerAddress = onChainOwner;
        }
      } catch (_ownerLookupError) {
        // Ignore owner lookup failures and continue with current validation path.
      }
    }

    if (
      resolvedReferralOwnerAddress &&
      account &&
      ethers.isAddress(resolvedReferralOwnerAddress) &&
      ethers.isAddress(account) &&
      ethers.getAddress(resolvedReferralOwnerAddress) === ethers.getAddress(account)
    ) {
      helperToast.error(t`You can't use your own referral code.`);
      return;
    }

    setIsSubmitting(true);

    try {
      const tx = await setTraderReferralCodeByUser(currentChainId, referralCode, signer, {
        account,
        successMsg: isEdit ? t`Referral code updated!` : t`Referral code added!`,
        failMsg: isEdit ? t`Referral code updated failed.` : t`Adding referral code failed.`,
        setPendingTxns,
        pendingTxns,
      });
      if (callAfterSuccess) {
        callAfterSuccess();
      }
      const receipt = await tx.wait();
      if (receipt.status === 1) {
        if (!userReferralCodeString && account) {
          try {
            if (!isAuthenticated) {
              const session = await signIn();
              if (!session) {
                helperToast.error(t`Referral linked on-chain, but sign-in is required to sync. Please sign in and retry.`);
                return;
              }
            }

            const referralOwnerAddress =
              resolvedReferralOwnerAddress ||
              (await getReferralCodeOwner(currentChainId, encodeReferralCode(referralCode)));

            await upsertTrader({
              address: account,
              referredBy: referralCode,
              referredByAddress:
                referralOwnerAddress && !isAddressZero(referralOwnerAddress) ? referralOwnerAddress : undefined,
            });
          } catch (syncError) {
            helperToast.error(t`Referral linked on-chain, but backend sync failed. Please refresh and retry.`);
            return;
          }
        }

        setReferralCode("");
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error);
    } finally {
      setIsSubmitting(false);
      setIsValidating(false);
    }
  }

  useEffect(() => {
    let cancelled = false;
    async function checkReferralCode() {
      if (debouncedReferralCode === "" || !REFERRAL_CODE_REGEX.test(debouncedReferralCode)) {
        setIsValidating(false);
        setReferralCodeExists(false);
        return;
      }

      setIsValidating(true);
      const codeExists = await validateReferralCodeExists(debouncedReferralCode, currentChainId);
      if (!cancelled) {
        setReferralCodeExists(codeExists);
        setIsValidating(false);
      }
    }
    checkReferralCode();
    return () => {
      cancelled = true;
    };
  }, [debouncedReferralCode, currentChainId]);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  useEffect(() => {
    if (userReferralCodeString) {
      return;
    }
    if (!storedReferralCode) {
      return;
    }
    setReferralCode((prev) => prev || storedReferralCode);
  }, [storedReferralCode, userReferralCodeString]);

  const shouldShowInviteDetails =
    !userReferralCodeString && referralCodeExists && !!lookupReferrer && debouncedReferralCode !== "";

  return (
    <div className="flex flex-col gap-5">
      <p className="pl-11 text-12 font-medium leading-[1.5] text-gray-lighter">
        <Trans>Enter a referral code below</Trans>
      </p>
      <form onSubmit={handleSubmit} className="flex w-full items-center gap-11">
        {!userReferralCodeString ? (
          <input
            ref={inputRef}
            disabled={isSubmitting}
            type="text"
            placeholder={t`Enter referral code`}
            maxLength={MAX_REFERRAL_CODE_LENGTH}
            className="flex-1 rounded-[28px] border border-solid border-white-alpha-4 bg-white-alpha-2 py-[9.5px] pl-[22px] pr-[11px] text-14 text-white placeholder:text-white-alpha-40 focus:border-primary-alpha-darker"
            value={referralCode}
            onChange={({ target }) => {
              const { value } = target;
              setReferralCode(value);
            }}
          />
        ) : (
          <ReferralCode code={userReferralCodeString} />
        )}
        {userReferralCodeString ? (
          <Button variant="primary" type="button" className="w-[126px] !py-8" disabled>
            <span className="font-medium">
              <Trans>Edit</Trans>
            </span>
          </Button>
        ) : (
          <div className="w-max">
            <Button
              variant="primary"
              type="submit"
              className="w-[126px] !py-8"
              disabled={!isPrimaryEnabled() || !referralCodeExists}
            >
              <span className="font-medium">{getPrimaryText()}</span>
            </Button>
          </div>
        )}
      </form>
      {isSelfReferral && (
        <p className="text-12 font-medium text-red-alpha-100">
          <Trans>You can't use your own referral code.</Trans>
        </p>
      )}
      {!isSelfReferral && !referralCodeExists && referralCode && !isValidating && (
        <p className="text-12 font-medium text-red-alpha-100">
          <Trans>Referral Code does not exist</Trans>
        </p>
      )}
      {shouldShowInviteDetails && (
        <div className="text-12 font-medium leading-[1.5] text-gray-lighter">
          <Trans>Invited by </Trans>
          <TooltipWithPortal
            position="bottom-start"
            handle={<span className="text-white">{shortenAddress(lookupReferrer!, 13)}</span>}
            content={<span className="font-medium">{lookupReferrer}</span>}
          />
          {discountPercent !== undefined ? (
            <>
              <span> · </span>
              <Trans>Estimated discount</Trans> <span className="text-white">{discountPercent.toFixed(2)}%</span>
            </>
          ) : (
            <>
              {/* <span> · </span>
              <Trans>Discount depends on inviter tier</Trans> */}
            </>
          )}
          {totalRebate !== undefined && (
            <>
              <span> · </span>
              <Trans>Total rebate</Trans> <span className="text-white">{(Number(totalRebate) / 100).toFixed(2)}%</span>
            </>
          )}
        </div>
      )}
    </div>
  );
}
export default JoinReferralCode;

const ReferralCode = ({ code }: { code?: string }) => {
  const [copied, setCopied] = useState(false);
  const idTimeout = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (copied) {
      idTimeout.current = setTimeout(() => {
        setCopied(false);
      }, 2000);
    }
    return () => {
      if (idTimeout.current) {
        clearTimeout(idTimeout.current);
        idTimeout.current = null;
      }
    };
  }, [copied]);

  return (
    <div className="flex flex-1 items-center gap-12">
      <span className="!text-[18px] font-medium leading-[1] text-gray-lightest">{code}</span>
      {copied ? (
        <CheckIcon className="size-23 text-gray-lightest" />
      ) : (
        <CopyIcon
          className="size-23 cursor-pointer text-gray-lightest"
          onClick={async () => {
            setCopied(true);
            try {
              await navigator.clipboard.writeText(getReferralCodeTradeUrl(code || ""));
              helperToast.success(t`Referral link copied to clipboard!`);
            } catch (error) {
              setCopied(false);
              helperToast.error(t`Failed to copy referral link.`);
            }
          }}
        />
      )}
    </div>
  );
};
